import React, { useEffect, useRef, useState, memo, useCallback } from "react";

import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import KeyIcon from "@mui/icons-material/Key";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Popper from "@mui/material/Popper";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";

import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import * as lodash from "lodash";
import { useDebounce } from "use-debounce";
import parse from "html-react-parser";

import { FileUploadPreview } from "./FileUploadPreview";
import { PasswordInput } from "./Password";
import * as u from "./utility";
import log from "./logger";
import { encrypt } from "./crypto";
import { Cipher } from "./Cipher";
import {
  useHttpPostAndProject,
  useUploadFile,
  useProjectCaptions,
  useHttpPost,
} from "./hooks";

import fuzzysort from "fuzzysort";
import { DarkStoryPreview } from "./StoryDetails";
import { StoryItem } from "./StoryItem";
import { MessageItem, FileUpload } from "./MessageItem";
import * as heic from "heic-convert/browser";
import { MAX_FILE_SIZE_PREVIEW, MAX_NUMBER_UPLOADED_FILES } from "./constants";
import { storeFileInCache } from "./filecache";
import { categorizeFilesForDisplay } from "./FileUploadPreview";
import * as riki from "jsriki";
import useResizeObserver from "@react-hook/resize-observer";
import TextareaAutosize from "react-textarea-autosize";
import { diffStrings } from "./diff";
import { preparseTextWithMath } from "./Markdown";
import chardet from "chardet";

export const MAX_MESSAGE_SIZE = 4000;
export const TEXT_INPUT_FONT_SIZE = 14;

export type StoryRef = {
  PK: string;
  SK: string;
};

const determineMimetype = async (f: File) => {
  if (!f.type) {
    try {
      const buffer = await f.arrayBuffer();
      let encoding = chardet.detect(new Uint8Array(buffer));

      if (encoding) {
        const str = new TextDecoder(encoding).decode(buffer);
        return u.determineTextMimeType(str);
      }
    } catch (e) {}
    return "";
  } else if (f.type.startsWith("text/")) {
    try {
      const buffer = await f.arrayBuffer();
      let encoding = chardet.detect(new Uint8Array(buffer));

      if (encoding) {
        const str = new TextDecoder(encoding).decode(buffer);
        return u.determineTextMimeType(str, f.type);
      }
    } catch (e) {}
  }

  return f.type;
};

export const getFilesFromDataTransfer = (
  dataTransfer: DataTransfer,
  set: React.Dispatch<React.SetStateAction<File[] | undefined>>
) => {
  const files: File[] = [];

  let numFiles = 0;
  const setFile = (f: File) => {
    set((prev) => {
      if (!prev) {
        return [f];
      } else if (prev.length < MAX_NUMBER_UPLOADED_FILES + 1) {
        if (!prev.find((e) => e.name === f.name && e.size === f.size)) {
          return [...prev, f];
        }
      }
      return prev;
    });
  };

  const scanFiles = (entry: FileSystemEntry, path: string) => {
    if (numFiles >= MAX_NUMBER_UPLOADED_FILES + 1) {
      return;
    }

    if (entry) {
      if (entry.isFile) {
        (entry as FileSystemFileEntry).file((f) => {
          if (numFiles < MAX_NUMBER_UPLOADED_FILES + 1) {
            numFiles += 1;
            if (f.size < MAX_FILE_SIZE_PREVIEW) {
              determineMimetype(f).then((mimetype) => {
                if (!mimetype) {
                  setFile(f);
                } else {
                  setFile(new File([f], f.name, { type: mimetype }));
                }
              });
            } else {
              setFile(f);
            }
          }
        });
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        reader.readEntries(function (entries) {
          entries.forEach((e) => scanFiles(e, path + e.name + "/"));
        });
      }
    }
  };
  for (const item of dataTransfer.items) {
    if (item.kind !== "file") {
      continue;
    }

    const e = item.webkitGetAsEntry();
    if (e) {
      scanFiles(e, "");
    }
  }
};

export const convertHeicFiles = async (files: FileList | File[]) => {
  let converted: FileUpload[] = [];

  for (let f of files) {
    if (f.type === "image/heic") {
      try {
        const buffer: Uint8Array = new Uint8Array(await f.arrayBuffer());
        const images = await heic.all({ format: "JPEG", buffer, quality: 0.8 });

        for (let img of images) {
          const outputBuffer = await img.convert();
          const name = f.name + ".jpeg";
          const out = new File([outputBuffer], name, { type: "image/jpeg" });
          converted.push({ original: f, preview: out });
          break;
        }
      } catch (e) {
        converted.push({ original: f });
      }
    } else {
      converted.push({ original: f });
    }
  }

  return converted;
};

export const processUploadedFiles = async (
  files: FileList | File[],
  selectedFiles: FileUpload[],
  setSelectedFiles: (value: React.SetStateAction<FileUpload[]>) => void,
  onNewUpload?: (files: FileUpload[]) => void
) => {
  if (!files.length) {
    return;
  }

  const isDuplicate = (file: File) => {
    for (let f of selectedFiles) {
      if (file.name === f.original.name && file.size === f.original.size) {
        return true;
      }
    }
    return false;
  };

  const updateState = (files: FileUpload[]) => {
    let newFiles: FileUpload[] = [];
    for (let fileToUpload of files) {
      if (!isDuplicate(fileToUpload.original)) {
        if (validateNotifyFileSize(fileToUpload.original)) {
          newFiles.push(fileToUpload);
        }
      }
    }

    if (newFiles.length > 0) {
      validateNotifyNumberOfFiles([...selectedFiles, ...newFiles]);

      setSelectedFiles((prev) => {
        const uniq: FileUpload[] = [];
        for (const f of newFiles) {
          if (
            !prev.find(
              (e) =>
                e.original.name === f.original.name &&
                e.original.size === f.original.size
            )
          ) {
            uniq.push(f);
          }
        }

        if (uniq.length > 0 && prev.length < MAX_NUMBER_UPLOADED_FILES) {
          const maxAllowed = MAX_NUMBER_UPLOADED_FILES - prev.length;
          const allowed = uniq.slice(0, maxAllowed);
          if (onNewUpload) {
            onNewUpload(allowed);
          }
          return [...prev, ...allowed];
        } else {
          return prev;
        }
      });
    }
  };

  const convertibleHeic = (f: File) =>
    f.type === "image/heic" && f.size < MAX_FILE_SIZE_PREVIEW;
  const [heicFiles, otherFiles] = lodash.partition(files, convertibleHeic);

  convertHeicFiles(heicFiles).then((files: FileUpload[]) => {
    updateState(files);
  });

  updateState(otherFiles.map((f) => ({ original: f })));
};

export const sortCondition = (
  a: MessageItem,
  b: MessageItem,
  order: boolean
) => {
  const lt = order ? -1 : 1;

  // for some messages we want them to stay in place becase the surrounding contenx matters
  // think about edited comment or edited slack message
  // bug for other context does not matter as much and we'd want to refrect that a message
  // has changed by placing it a different place in the thread, think a pull request diff
  // aftter a pull request receives a new commit it's diff will update and we'd want to show a
  // new diff after the second commit
  //
  // with "commit" there was a bug, we did assign st_edit to every commit by mistake and we
  // were getting those editt i from pull request, not the bug is fixed, but i'll leave it for
  // the old data
  const ka =
    a.st_edit && a.st_type !== "diff" && a.st_type !== "commit"
      ? a.st_edit + a.SK
      : a.SK;
  const kb =
    b.st_edit && b.st_type !== "diff" && a.st_type !== "commit"
      ? b.st_edit + b.SK
      : b.SK;

  if (ka < kb) {
    return lt;
  } else if (ka === kb) {
    return 0;
  } else {
    return -lt;
  }
};

export const sortMessages = (messages: MessageItem[], order: boolean) => {
  if (!messages) {
    return messages;
  }

  let sorted = Array.from(messages);
  sorted.sort((a, b) => {
    return sortCondition(a, b, order);
  });
  return sorted;
};

function UploadErrorToast({ onRetry }: { onRetry: () => void }) {
  return (
    <Stack direction={"row"} alignItems={"center"}>
      <Typography fontSize={12}>File upload error</Typography>
      <Button onClick={onRetry} size="small">
        RETRY
      </Button>
    </Stack>
  );
}

type UpdateRefsFunc = (prev: StoryRef[]) => StoryRef[];

export function handleFilesFromPasteEvent(
  e: React.ClipboardEvent<HTMLInputElement>,
  processFiles: (files: File[]) => void,
  setRefs: (arg: StoryRef[] | UpdateRefsFunc) => void
) {
  let clipboardItems = e.clipboardData.items;

  let hasFilesOrDirs = false;
  for (let i = 0; i < clipboardItems.length; ++i) {
    const item = clipboardItems[i];
    if (item.kind === "file") {
      hasFilesOrDirs = true;
    }
  }

  if (hasFilesOrDirs) {
    let files: File[] | undefined = undefined;
    type UpdFunc = (prev: File[] | undefined) => File[] | undefined;
    const updateFiles = (f: undefined | File[] | UpdFunc) => {
      if (f) {
        if (typeof f === "function") {
          files = f(files);
        } else {
          files = f;
        }
        if (files) {
          processFiles(files);
        }
      }
    };

    getFilesFromDataTransfer(e.clipboardData, updateFiles);
  }

  let files: File[] = [];
  // lastly handle html. the processFiles has to be called once
  for (let i = 0; i < clipboardItems.length && !hasFilesOrDirs; ++i) {
    const item = clipboardItems[i];
    if (item.kind === "string") {
      if (item.type === "text/plain") {
        const txt = e.clipboardData.getData("text");
        if (txt.length > MAX_MESSAGE_SIZE) {
          const largeTextFile = new File([txt], "description.txt", {
            type: u.determineTextMimeType(txt),
          });
          files.push(largeTextFile);
        }
      } else if (item.type === "text/html") {
        const downloadHtmlImages = async (
          images: HTMLCollectionOf<HTMLImageElement>,
          files: File[]
        ) => {
          for (let i = 0; i < images.length; ++i) {
            const img = images.item(i);
            if (!img) {
              continue;
            }

            const getTopstorieName = (img: HTMLImageElement) => {
              const attr = img.attributes.getNamedItem("st_filename");
              return attr ? attr.nodeValue : undefined;
            };

            try {
              const response = await fetch(img.src);
              const data = await response.blob();
              if (data.type.startsWith("image/")) {
                let nameFromUrl =
                  getTopstorieName(img) ||
                  (!img.src.startsWith("data:") && img.src.split("/").pop()) ||
                  uuidv4();

                nameFromUrl = u.sanitizeFileName(nameFromUrl);

                const filetype = data.type.split("/").pop();
                if (!nameFromUrl.endsWith("." + filetype)) {
                  nameFromUrl += "." + filetype;
                }

                const imageFile = new File([data], nameFromUrl, {
                  type: data.type,
                });
                files.push(imageFile);
              }
            } catch (e) {
              log.debug(e);
            }
          }

          if (files.length > 0) {
            processFiles(files);
          }
        };

        const parser = new DOMParser();
        const html = e.clipboardData.getData("text/html");
        const doc = parser.parseFromString(html, "text/html");
        if (doc.images.length > 0) {
          downloadHtmlImages(doc.images, files);
          files = [];
        }

        let refs: StoryRef[] = [];
        extractRefsFromHtml(doc, refs);

        if (refs.length > 0) {
          setRefs((prev: StoryRef[] | undefined) => {
            let res: StoryRef[] = [];
            let seen = new Set();

            const proc = (refs: StoryRef[]) => {
              for (let r of refs) {
                const key = r.PK + ":" + r.SK;
                if (!seen.has(key)) {
                  seen.add(key);
                  res.push(r);
                }
              }
            };

            if (prev) {
              proc(prev);
            }

            proc(refs);
            return prev && prev.length === res.length ? prev : res;
          });
        }
      }
    }
  }

  if (files.length > 0 || hasFilesOrDirs) {
    processFiles(files);
    e.preventDefault();
  }
}

function TextToast({ text }: { text: string }) {
  return <Typography fontSize={12}>{text}</Typography>;
}

export function validateNotifyFileSize(file: File) {
  if (file.size > 128 * 1024 * 1024) {
    let abbrev = file.name;
    if (abbrev.length > 16) {
      abbrev = abbrev.slice(0, 13) + "...";
    }

    const text = `Sorry, the file "${abbrev}" is too big.`;
    toast.error(<TextToast text={text} />);
    return false;
  }

  return true;
}

export function validateNotifyNumberOfFiles(files: FileUpload[]) {
  if (files.length > MAX_NUMBER_UPLOADED_FILES) {
    const text = `Sorry, you can't attach more than ${MAX_NUMBER_UPLOADED_FILES} files.`;
    toast.error(<TextToast text={text} />);
    return false;
  }
  return true;
}

function extractRefsFromHtml(doc: Document | Element, refs: StoryRef[]) {
  for (const child of doc.children) {
    const pk = child.attributes.getNamedItem("st_pk");
    if (pk && pk.nodeValue) {
      const sk = child.attributes.getNamedItem("st_sk");
      if (sk && sk.nodeValue) {
        const ref = { PK: pk.nodeValue, SK: sk.nodeValue };

        const found = refs.find((e) => lodash.isEqual(ref, e));
        if (!found) {
          refs.push(ref);
        }
      }
    }
    extractRefsFromHtml(child, refs);
  }
}

const getCacheKey = (item: StoryItem) => {
  const key = `SendMessageBox:${item.PK}:${item.SK}`;
  return key;
};

export const unfinishedMessageFromCache = (item: StoryItem): string => {
  const message = u.readStageStorage(getCacheKey(item), "");
  return message || "";
};

const saveMessageInCache = (item: StoryItem, message: string) => {
  if (message) {
    u.writeStageStorage(getCacheKey(item), message);
  } else {
    u.removeFromStageStorage(getCacheKey(item));
  }
};

const clearMessageInCache = (item: StoryItem) => {
  saveMessageInCache(item, "");
};

type FuzzyResult = {
  text: string;
  highlight: string;
};

const fuzzySearch = (input: string, captions: any): FuzzyResult[] => {
  const targets = Object.keys(captions);
  const res = fuzzysort.go(input, targets);
  return res.slice(0, 100).map((e) => {
    return {
      text: e.target,
      highlight: e.highlight("<font color='burlywood'><b>", "</b></font>"),
    };
  });
};

export type SuccessfulUpload = {
  file: FileUpload;
  uri: string;
};

export const cacheUploadedFiles = async (
  projectName: string,
  uploads: SuccessfulUpload[]
) => {
  const promises: Promise<void>[] = [];
  const { previews } = categorizeFilesForDisplay(
    uploads.map((e) => e.file.original)
  );

  // if available should store only the preview file in cache, otherwise the original
  for (const preview of previews) {
    const upload = uploads[preview.index];
    let previewFile: File | undefined = upload.file.original;
    let previewUrl: string | undefined = upload.uri;

    if (upload.file.preview) {
      // i guess it's better to fail early with unsupported previews
      // because i am going to hardcode some urls that have to be synchronized with the backend
      // see downloader.py
      if (
        upload.file.original.type === "image/heic" &&
        upload.file.preview.name.endsWith(".jpeg")
      ) {
        previewFile = upload.file.preview;
        previewUrl += ".jpeg";
      } else {
        previewFile = undefined;
        previewUrl = undefined;
      }
    }

    if (previewFile && previewUrl && previewFile.size < MAX_FILE_SIZE_PREVIEW) {
      promises.push(storeFileInCache(projectName, previewUrl, previewFile));
    }
  }
  if (promises.length > 0) {
    await Promise.all(promises);
  }
};

type SuggestionsProps = {
  input: string;
  captions: { [key: string]: string }; // Map<string, string>
  onSelected: (suggestion: string) => void;
  onHighlight?: (suggestion: string) => void;
  suggestionsRef?: { current?: FuzzyResult[] };
  sx: any;
  selectedIndex: number;
  noScrollIntoView?: boolean;
};

export function Suggestions({
  input,
  captions,
  onSelected,
  onHighlight,
  suggestionsRef,
  sx,
  selectedIndex,
  noScrollIntoView,
}: SuggestionsProps) {
  const [found, setFound] = useState<FuzzyResult[]>([]);
  const [highestRankingStory, setHighestRankingStory] = useState<StoryItem>();
  const httpPost = useHttpPost();
  const [hoverStory, setHoverStory] = useState<StoryItem>();
  const [debouncedInput] = useDebounce(input, 200);
  const menuRef = useRef<HTMLUListElement>(null);

  const searchStory = async (
    storyCaption: string
  ): Promise<StoryItem | undefined> => {
    try {
      const req = { search: true, query: storyCaption };
      const resp = await httpPost("stories", req);
      const searchResult = resp.data.search;
      if (searchResult) {
        return searchResult[0];
      }
    } catch (e) {}
    return undefined;
  };

  useEffect(() => {
    const input = debouncedInput;
    const found = fuzzySearch(input, captions);
    setFound(found);
    if (suggestionsRef) {
      suggestionsRef.current = found;
    }
    if (found.length > 0) {
      const request = async (storyCaption: string) => {
        setHighestRankingStory(await searchStory(storyCaption));
      };
      const index = lodash.clamp(selectedIndex ?? 0, 0, found.length - 1);
      request(found[index].text);
    } else {
      setHighestRankingStory(undefined);
    }

    if (/^-?\d+$/.test(input.trim())) {
      const request = async (searchInput: string) => {
        const story = await searchStory(searchInput);
        if (story) {
          setHighestRankingStory((prev) => (prev ? prev : story));

          const text = story.st_cap || input;

          // deduplicate first
          if (!found.find((e) => e.text === text)) {
            if (searchInput === input.trim()) {
              const suggestions = [...found, { text: text, highlight: text }];
              setFound(suggestions);

              if (suggestionsRef) {
                suggestionsRef.current = suggestions;
              }
            }
          }
        }
      };
      request(input.trim());
    }
  }, [debouncedInput, captions]);

  useEffect(() => {
    if (found.length > 0) {
      const request = async (storyCaption: string) => {
        setHighestRankingStory(await searchStory(storyCaption));
      };
      const index = lodash.clamp(selectedIndex ?? 0, 0, found.length - 1);

      const text = found[index].text;
      request(text);

      if (onHighlight) {
        onHighlight(text);
      }

      if (menuRef && menuRef.current && !noScrollIntoView) {
        const child = menuRef.current.children[index];
        const y = Math.max(
          (child as any).offsetTop -
            menuRef.current.clientHeight +
            2 * child.clientHeight,
          0
        );
        menuRef.current.scroll(0, y);
      }
    }
  }, [selectedIndex, found]);

  const handleMouseEnter = (item: string) => {
    const request = async (storyCaption: string) => {
      setHoverStory(await searchStory(storyCaption));
    };
    request(item);
  };

  const handleMouseLeave = (item: string) => {
    // setHoverStory((prev) => prev === item ? undefined : prev);
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.target as HTMLElement;
    // see [1167]
    if (el.tagName !== "B") {
      onSelected(el.innerText);
    } else {
      let parent = el.parentElement;
      if (
        parent &&
        parent.tagName === "FONT" &&
        parent.parentElement &&
        parent.parentElement.tagName === "DIV"
      ) {
        onSelected(parent.parentElement.innerText);
      } else {
        // TODO : do we want to raise an exception here?
        onSelected(el.innerText);
      }
    }
  };

  return (
    <>
      {found.length > 0 && (
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          sx={{
            backgroundColor: "#404040",
            color: "white",
            borderRadius: "5px",
            mt: 0.5,
            ...(sx ? sx : {}),
          }}
        >
          <Box sx={{ minWidth: "40%", overflow: "auto" }}>
            <MenuList
              // open={input ? true : false}
              ref={menuRef}
              sx={{ maxHeight: 300, overflow: "auto" }}
            >
              {found.map(({ text, highlight }, index) => {
                return (
                  <MenuItem
                    key={text}
                    onClick={handleClick}
                    sx={{ fontSize: 11 }}
                    onMouseEnter={() => handleMouseEnter(text)}
                    onMouseLeave={() => handleMouseLeave(text)}
                    selected={index === selectedIndex}
                  >
                    <div>{parse(highlight)}</div>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Box>
          {(hoverStory || highestRankingStory) && (
            <DarkStoryPreview
              item={hoverStory || highestRankingStory}
              fontSize={9}
            />
          )}
        </Stack>
      )}
    </>
  );
}

type SuggestionsPopperProps = {
  buffer: CursorBuffer;
  anchorRef?: { current?: HTMLElement };
  suggestionsRef: { current?: FuzzyResult[] };
  onSelected: (suggestion: string) => void;
  noScrollIntoView?: boolean;
  selectedIndex: number;
  onHighlight?: (suggestion: string) => void;
  options: { [key: string]: string };
};

export function SuggestionsPopper({
  buffer,
  anchorRef,
  suggestionsRef,
  onSelected,
  noScrollIntoView,
  selectedIndex,
  onHighlight,
  options,
}: SuggestionsPopperProps) {
  return (
    <>
      {buffer.text && anchorRef && anchorRef.current && (
        <Popper
          anchorEl={anchorRef.current}
          open={true}
          placement="bottom-start"
          style={{ zIndex: 10000 }}
          popperOptions={{
            modifiers: [
              {
                name: "flip",
                enabled: false,
              },
            ],
          }}
        >
          <Box sx={{ width: 500 }}>
            <Suggestions
              input={buffer.text}
              captions={options}
              onSelected={onSelected}
              suggestionsRef={suggestionsRef}
              selectedIndex={selectedIndex}
              noScrollIntoView={noScrollIntoView}
              onHighlight={onHighlight}
              sx={undefined}
            />
          </Box>
        </Popper>
      )}
    </>
  );
}

type CursorBuffer = {
  cursor?: number;
  text?: string;
  start?: number;
};

type MathExpr = {
  start: number;
  end: number;
  result: number;
  resultEnd: number;
  insertCount: number;
};

type Bounds = {
  start: number;
  end: number;
};

const calculateInPlace = (target: HTMLInputElement): MathExpr | undefined => {
  const text = target.value;
  let prefix = text;
  let postfix = "";
  if (target.selectionStart) {
    prefix = text.slice(0, target.selectionStart);
    postfix = text.slice(target.selectionStart);
  }

  if (prefix) {
    if (prefix[prefix.length - 1] !== "=") {
      throw new Error("something went wrong");
    }

    const res: { start: number; end: number; result: number } =
      riki.calculate(prefix);
    if (res !== undefined) {
      let resultStr = res.result.toString();
      if (prefix[prefix.length - 2] == " ") {
        resultStr = " " + resultStr;
      }

      if (postfix.length > 0 && postfix[0] != " ") {
        resultStr += " ";
      }

      target.value = prefix + resultStr + postfix;
      target.setSelectionRange(prefix.length, prefix.length + resultStr.length);

      const resultLen =
        resultStr[resultStr.length - 1] == " "
          ? resultStr.length - 1
          : resultStr.length;
      return {
        ...res,
        resultEnd: res.end + resultLen + 1,
        insertCount: resultStr.length,
      };
    }
  }

  return undefined;
};

export const clearElement = (el: HTMLDivElement) => {
  el.replaceChildren("");
};

export const clearInputWithMathHighlights = (el?: HTMLInputElement) => {
  if (!el) {
    return;
  }
  const highlightRef: HTMLDivElement | undefined = (el as any).mathHighlightRef;
  const mathState: MathState | undefined = (el as any).mathState;

  if (highlightRef) {
    clearElement(highlightRef);
  }

  if (mathState) {
    while (mathState.exprBounds.length) {
      mathState.exprBounds.pop();
    }
    mathState.prevInput = "";
    mathState.currentCalc = undefined;
  }
};

export const syncInputsWithMathHighlights = (
  from?: HTMLInputElement,
  to?: HTMLInputElement
) => {
  if (!from || !to || from == to) {
    return;
  }

  const div1: HTMLDivElement | undefined = (from as any).mathHighlightRef;
  const state1: MathState | undefined = (from as any).mathState;
  const container1: HTMLElement | undefined = (from as any).mathContainerRef;

  const div2: HTMLDivElement | undefined = (to as any).mathHighlightRef;
  const state2: MathState | undefined = (to as any).mathState;
  const container2: HTMLElement | undefined = (from as any).mathContainerRef;

  if (!div1 || !state1 || !div2 || !state2 || !container1 || !container2) {
    return;
  }

  while (state2.exprBounds.length > 0) {
    state2.exprBounds.pop();
  }
  state1.exprBounds.forEach((e) => state2.exprBounds.push(e));
  state2.prevInput = state1.prevInput;
  state2.currentCalc = state1.currentCalc;

  to.value = from.value;
};

export const drawMathHighlightsForMathInput = (inputEl?: HTMLInputElement) => {
  if (!inputEl) {
    return;
  }

  const mathState: MathState | undefined = (inputEl as any).mathState;
  const highlights: HTMLDivElement | undefined = (inputEl as any)
    .mathHighlightRef;
  const container: HTMLElement | undefined = (inputEl as any).mathContainerRef;
  if (mathState) {
    drawMathHightlights(
      inputEl.value,
      mathState.exprBounds,
      inputEl,
      container,
      highlights
    );
  }
};

const drawMathHightlights = (
  text: string,
  bounds: Bounds[],
  inputEl?: HTMLInputElement,
  container?: HTMLElement | null,
  highlightDiv?: HTMLDivElement
) => {
  if (container && highlightDiv && bounds.length > 0) {
    const sorted = bounds.sort((a, b) => a.start - b.start);
    const collapased: Bounds[] = [];
    for (const s of sorted) {
      if (collapased.length == 0) {
        collapased.push(s);
      } else if (s.start < collapased[collapased.length - 1].end) {
        collapased[collapased.length - 1].end = s.end;
      } else {
        collapased.push(s);
      }
    }

    const elements = [];

    let pos: number = 0;

    for (const e of collapased) {
      if (e.start < pos) {
        throw Error(
          `wrong position ${pos} vs ${JSON.stringify(
            e
          )} in '${text}': ${JSON.stringify(sorted)}`
        );
      }

      const prefix = text.slice(pos, e.start);
      const expr = text.slice(e.start, e.end);
      pos = e.end;

      if (prefix) {
        const prefixEl = document.createElement("span");
        prefixEl.innerText = prefix;
        elements.push(prefixEl);
      }

      const exprEl = document.createElement("span");
      exprEl.style.backgroundColor = "red";
      exprEl.style.borderRadius = "4px";
      exprEl.style.paddingTop = "2px";
      exprEl.style.paddingBottom = "2px";
      exprEl.innerText = expr;
      elements.push(exprEl);
    }

    // const postfix = text.slice(pos);
    // if (postfix) {
    //   const postfixEl = document.createElement("span");
    //   postfixEl.innerText = postfix;
    //   elements.push(postfixEl);
    // }

    const highlight = highlightDiv;
    highlight.replaceChildren(...elements);

    // const inputEl: HTMLTextAreaElement = (props.inputRef || inputRef).current;
    if (inputEl) {
      const inputStyle = window.getComputedStyle(inputEl);
      for (const k of inputStyle) {
        if (k.startsWith("font")) {
          highlight.style.setProperty(k, inputStyle.getPropertyValue(k));
        }
      }

      highlight.style.textIndent = inputStyle.textIndent;
      highlight.style.letterSpacing = inputStyle.letterSpacing;
      highlight.style.lineHeight = inputStyle.lineHeight;
      highlight.style.wordSpacing = inputStyle.wordSpacing;
      highlight.style.whiteSpace = inputStyle.whiteSpace;

      highlight.style.width = inputStyle.width;
      // highlight.style.left = inputRect.left + 'px';
      // highlight.style.top = inputRect.top + 'px';
      // highlight.style.bottom = inputRect.bottom + 'px';
      // highlight.style.right = inputRect.right + 'px';

      const containerRect = container.getBoundingClientRect();
      const inputRect = inputEl.getBoundingClientRect();

      const offset = inputRect.top - containerRect.top;
      highlight.style.top = offset + "px";
    }
  } else if (highlightDiv) {
    highlightDiv.replaceChildren();
  }
};

function HighlightBox({ divRef, sx }: { sx: any; divRef: any }) {
  return <Box sx={sx} ref={divRef} />;
}

type CalcResult = {
  event: React.ChangeEvent<HTMLInputElement>;
  result: MathExpr;
};

export type MathState = {
  currentCalc?: CalcResult;
  prevInput: string;
  exprBounds: Bounds[];
};

export function TextFieldWithSuggestions(props: any) {
  const [buffer, setBuffer] = useState<CursorBuffer>({});
  const suggestionsRef = useRef<FuzzyResult[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>();
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);
  const captions = useProjectCaptions();
  const highlightRef = useRef<HTMLDivElement>();
  const containerRef = useRef<HTMLDivElement>(null);
  const mathRef = useRef<MathState>({ prevInput: "", exprBounds: [] });
  const [containerSize, setContainerSize] = useState<ResizeObserverEntry>();

  const highlightSize = useResizeObserver(containerRef, (entry) => {
    if (mathRef.current.currentCalc) {
      setContainerSize(entry);
    }
  });

  useEffect(() => {
    const calc = mathRef.current.currentCalc;
    if (calc) {
      const target = calc.event.target;
      drawHighlights(target.value, calc.result ? [calc.result] : []);
    }
  }, [containerSize]);

  useEffect(() => {
    const inputEl: HTMLTextAreaElement = (props.inputRef || inputRef).current;
    if (inputEl) {
      // inject stuff into event so the parent component can handle our math and its highlighting
      // the reason is that the parent component has controls like sending the message out
      // which needs math boundaries and which will clear the highlight
      (inputEl as any).mathHighlightRef = highlightRef.current;
      (inputEl as any).mathState = mathRef.current;
      (inputEl as any).mathContainerRef = containerRef.current;
    }
  }, []);

  const updateInput = (value: string) => {
    if (inputRef?.current) {
      inputRef.current.value = value;
    }
  };

  const drawHighlights = (
    text: string,
    bounds: { start: number; end: number }[]
  ) => {
    const inputEl = (props.inputRef || inputRef).current;
    drawMathHightlights(
      text,
      bounds,
      inputEl,
      containerRef.current,
      highlightRef.current
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const value = target.value;
    const cursor = e.target.selectionStart;
    let calculatedResult: MathExpr | undefined = undefined;
    const nativeEvent = e.nativeEvent as any;
    let dirty = false;

    if (cursor !== null) {
      for (const b of mathRef.current.exprBounds) {
        if (b.start > mathRef.current.prevInput.length) {
          throw Error(
            `expr beyond boundaries ${JSON.stringify(b)} in '${
              mathRef.current.prevInput
            }'`
          );
        }
      }

      if (mathRef.current.exprBounds.length > 0) {
        let toDelete: Set<number> | undefined = undefined;
        if (nativeEvent.inputType !== "deleteContentBackward") {
          const prev = mathRef.current.prevInput;
          const cur = value;
          const [pos, addCount, removeCount] = diffStrings(prev, cur, cursor);
          // console.log(`${pos} +${addCount} -${removeCount}`);

          mathRef.current.exprBounds.forEach((e, idx) => {
            // console.log(`${pos} vs ${JSON.stringify(e)}`);
            if (
              (removeCount &&
                pos < e.start &&
                pos + removeCount - 1 < e.start) ||
              (!removeCount && pos <= e.start)
            ) {
              e.start += addCount - removeCount;
              e.end += addCount - removeCount;
              dirty = true;
              // console.log(`shift ${JSON.stringify(e)}`);
            } else if (
              u.intervalsIntersectInclusive(
                e.start,
                e.end - 1,
                pos,
                pos + removeCount
              )
            ) {
              if (!toDelete) {
                toDelete = new Set();
              }
              toDelete.add(idx);
              dirty = true;
              // console.log(`delete ${JSON.stringify(e)}`);
            } else {
              // console.log(`keep ${JSON.stringify(e)}`);
            }
          });
        } else {
          // deleteContentBackward
          // multiple characters can be deleted with deleteContentBackward

          const deleteCount = mathRef.current.prevInput.length - value.length;
          const deleteFirst = cursor;
          const deleteLast = deleteFirst + deleteCount - 1; // the boundary is inclusive

          // console.log(
          //   `delete count ${deleteCount} ${deleteFirst}:${deleteLast} in ${JSON.stringify(
          //     exprBoundsRef.current
          //   )}`
          // );
          mathRef.current.exprBounds.forEach((e, idx) => {
            // deleting happens within the boundaries of the expression
            // drop the entire experssion then
            const intersection = u.intervalsIntersectInclusive(
              e.start,
              e.end - 1,
              deleteFirst,
              deleteLast
            );

            // console.log(
            //   `intersection ${intersection}: ${e.start}:${
            //     e.end - 1
            //   } vs ${deleteFirst}:${deleteLast}`
            // );

            if (intersection) {
              const prev = mathRef.current.prevInput;
              let [a, b] = intersection;
              let allWhitespace = u.isFilledSubstring(prev, " ", a, b - a + 1);

              if (!allWhitespace) {
                if (!toDelete) {
                  toDelete = new Set();
                }
                toDelete.add(idx);
                dirty = true;
                // console.log(`delete ${JSON.stringify(e)}`);
              } else {
                const shift = b - a + 1;
                const shiftStart = deleteLast - deleteFirst + 1 - shift;
                e.start -= shiftStart;
                e.end -= shift;
                // console.log(
                //   `shift by ${shiftStart}:${shift} ${JSON.stringify(e)}`
                // );
              }
              dirty = true;
            } else if (cursor < e.start) {
              // and here we're deleting characters preceeding the math expression
              // so we merely shift the expression boundaries
              if (e.start < deleteCount || e.end < deleteCount) {
                throw Error(
                  `negative bounds ${deleteCount} in ${JSON.stringify(e)}: '${
                    mathRef.current.prevInput
                  }' --> '${value}'`
                );
              }
              e.start -= deleteCount;
              e.end -= deleteCount;
              dirty = true;
              // console.log(`shift ${JSON.stringify(e)}`);
            } else {
              // console.log(`keep ${JSON.stringify(e)}`);
            }
            if (cursor == e.end) {
              dirty = true;
            }
          });
        }

        if (toDelete) {
          mathRef.current.exprBounds = mathRef.current.exprBounds.filter(
            (e, idx) => !toDelete!.has(idx)
          );
        }
      }

      if (nativeEvent.data === "[") {
        setBuffer({ start: cursor, cursor: cursor, text: "" });
      } else if (nativeEvent.data === "]") {
        setBuffer({});
      } else if (nativeEvent.data === "=") {
        calculatedResult = calculateInPlace(e.target);
      } else if (nativeEvent.inputType === "deleteContentBackward") {
        setBuffer((prev: CursorBuffer) => {
          if (
            prev.start &&
            cursor >= prev.start &&
            prev.cursor === cursor + 1
          ) {
            const text = prev.text!.slice(0, -1);
            return { cursor: cursor, start: prev.start, text: text };
          } else if (lodash.isEmpty(prev)) {
            return prev;
          } else {
            return {};
          }
        });

        const text = e.target.value;
        if (
          mathRef.current.currentCalc === undefined &&
          text.length > 0 &&
          cursor > 0 &&
          text[cursor - 1] === "="
        ) {
          calculatedResult = calculateInPlace(e.target);
        }
      } else {
        // check buffer length if it exceeds max caption size
        setBuffer((prev: CursorBuffer) => {
          if (prev.cursor && prev.cursor + 1 === cursor) {
            const text = prev.text + nativeEvent.data;
            return { cursor: cursor, start: prev.start, text: text };
          } else if (lodash.isEmpty(prev)) {
            return prev;
          } else {
            return {};
          }
        });
        setSelectedSuggestion(0);
      }
    }

    if (calculatedResult) {
      mathRef.current.currentCalc = { event: e, result: calculatedResult };

      if (cursor !== null) {
        // can cursor be null here?
        // inserting calculated result shifts math expressions that follow
        for (const expr of mathRef.current.exprBounds) {
          if (cursor - 1 <= expr.start) {
            const offset = calculatedResult.insertCount;
            expr.start += offset;
            expr.end += offset;
          }
        }
      }

      mathRef.current.exprBounds.push({
        start: calculatedResult.start,
        end: calculatedResult.resultEnd,
      });
    }

    if (e !== mathRef.current.currentCalc?.event) {
      if (mathRef.current.currentCalc) {
        dirty = true;
      }

      mathRef.current.currentCalc = undefined;
    }

    if (calculatedResult || dirty) {
      if (calculatedResult) {
        const highlights = mathRef.current.exprBounds.filter(
          (e) => e.start != calculatedResult!.start
        );
        highlights.push(calculatedResult);
        drawHighlights(e.target.value, highlights);
      } else {
        drawHighlights(e.target.value, mathRef.current.exprBounds);
      }
    }

    if (props.onChange) {
      props.onChange(e);
    }

    mathRef.current.prevInput = target.value;
  };

  const selectPrev = () => {
    if (suggestionsRef.current && suggestionsRef.current.length) {
      const index = Math.max(0, selectedSuggestion - 1);
      setSelectedSuggestion(index);
    }
  };

  const selectNext = () => {
    if (suggestionsRef.current && suggestionsRef.current.length > 0) {
      const index = Math.min(
        suggestionsRef.current.length - 1,
        selectedSuggestion + 1
      );
      setSelectedSuggestion(index);
    }
  };

  const clearHighlights = () => {
    const ref = (props.inputRef || inputRef).current;
    clearInputWithMathHighlights(ref);
  };

  const handleKeyDown = (e: any) => {
    let shouldClearHighlights = false;
    if ((e.key === "Tab" || e.key === "Enter") && buffer.text) {
      // const suggest = fuzzySearch(buffer.text, captions);
      if (suggestionsRef?.current?.length > 0) {
        const index = lodash.clamp(
          selectedSuggestion,
          0,
          suggestionsRef.current.length - 1
        );
        const suggestion = suggestionsRef.current[index].text;
        handleSuggestionSelected(suggestion);
        e.preventDefault();
      }
    } else if (
      (e.key === "ArrowUp" || e.key === "ArrowDown") &&
      lodash.isEmpty(buffer)
    ) {
      props.onKeyDown(e);
    } else if (e.key === "ArrowUp" || (e.ctrlKey && e.key === "k")) {
      selectPrev();
      e.preventDefault();
    } else if (e.key === "ArrowDown" || (e.ctrlKey && e.key === "j")) {
      selectNext();
      e.preventDefault();
    } else if (e.key === "Escape" && buffer.text) {
      setBuffer({});
    } else if (props.onKeyDown) {
      if (e.key === "Enter" && !e.shiftKey) {
        shouldClearHighlights = true;
      } else if (e.key === "Tab" && mathRef.current.currentCalc) {
        const target = mathRef.current.currentCalc.event.target;

        if (target.selectionStart !== null && target.selectionEnd !== null) {
          target.setSelectionRange(target.selectionEnd, target.selectionEnd);
          e.preventDefault();
          // clearHighlights();
          drawHighlights(e.target.value, mathRef.current.exprBounds);
        }
      }
      props.onKeyDown(e);
    }
    if (shouldClearHighlights) {
      clearHighlights();
    }
  }; // handleKeyDown

  const handleBlur = (e: any) => {
    // give a chance to other events to be handled
    // like when you click on a suggestion, that will immediately trigger blur
    // and you'll loose an event containing the clicked suggestion
    setTimeout(() => {
      setBuffer({});
    }, 300);

    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const handleSuggestionSelected = (value: string) => {
    const ref = (props.inputRef || inputRef).current;
    ref.focus();

    const preText = ref.value.slice(0, buffer.start) + value + "]";
    const postText = ref.value.slice(buffer.cursor);
    ref.value = preText + postText;
    ref.setSelectionRange(preText.length, preText.length);

    updateInput(preText + postText);
    setBuffer({});
  };

  const handleMouseDown = () => {
    setBuffer({});
  };

  const cleanProps = () => {
    let res = { ...props };
    return res;
  };

  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => {
    if (props.onFocus) {
      props.onFocus(e);
    }

    const inputEl = (props.inputRef || inputRef).current;
    drawMathHighlightsForMathInput(inputEl);
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          display: "inline-block",
          width: "100%",
          height: "100%",
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            background: "transparent",
            top: 0,
            right: 0,
            zIndex: 10,
            position: "relative",
            width: "100%",
          }}
        >
          <TextField
            {...cleanProps()}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onMouseDown={handleMouseDown}
            inputRef={props.inputRef || inputRef}
            onFocus={handleFocus}
          />
          <SuggestionsPopper
            anchorRef={props.inputRef || inputRef}
            buffer={buffer}
            suggestionsRef={suggestionsRef}
            onSelected={handleSuggestionSelected}
            selectedIndex={selectedSuggestion}
            options={captions}
          />
        </Box>
        <MemoHighlightBox
          divRef={highlightRef}
          sx={{
            zIndex: 1,
            position: "absolute",
            opacity: "20%",
            color: "rgba(0, 0, 0, 1)", // transparent text
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            wordWrap: "break-word",
          }}
        />
      </Box>
    </>
  );
}

type UpdateStringFunc = (str: string) => string;
export type NotifyMessage = { ts: string; msg: MessageItem; text?: string };

type SendMessageBoxProps = {
  item: StoryItem;
  setMessages: (arg: any) => void;
  id: string;
  onSent: (message?: NotifyMessage) => void;
  onSending?: () => void;
  droppedFiles?: File[] | FileList;
  onFileRemoved: () => void;
  selectedFiles: FileUpload[];
  setSelectedFiles: (value: React.SetStateAction<FileUpload[]>) => void;
  message: string;
  password: string;
  setPassword: (password: string) => void;
  previousMessage: string;
  onBlur: (event: any) => void;
  onFocus: (event: any) => void;
  onVisible?: (visible: boolean) => void;
  onChange?: (value: string) => void;
  inputRef: React.MutableRefObject<HTMLInputElement | undefined>;
};

export function SendMessageBox({
  item,
  setMessages,
  id,
  onSent,
  droppedFiles,
  onFileRemoved,
  selectedFiles,
  setSelectedFiles,
  message,
  password,
  setPassword,
  previousMessage,
  onBlur,
  onFocus,
  onVisible,
  onSending,
  onChange,
  inputRef,
}: SendMessageBoxProps) {
  const [disabled, setDisabled] = useState<boolean>(
    !unfinishedMessageFromCache(item)
  );
  const [refs, setRefs] = useState<StoryRef[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [httpPost, projectName] = useHttpPostAndProject();
  const uploadToS3 = useUploadFile();

  const updateInput = (value: string) => {
    if (inputRef?.current) {
      inputRef.current.value = value;
      inputRef.current.style.height = inputRef.current.scrollHeight + "px";

      if (onChange) {
        onChange(value);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDisabled(value.length === 0 && selectedFiles.length === 0);
    if (onChange) {
      onChange(value);
    }
  };

  const getInputMessage = () => {
    return inputRef?.current?.value || "";
  };

  const processFiles = (files: FileList | File[]) => {
    processUploadedFiles(files, selectedFiles, setSelectedFiles);
  };

  const handleSend = () => {
    const message = getInputMessage();
    if (
      (!message || message.length === 0) &&
      (!selectedFiles || selectedFiles.length === 0)
    ) {
      return;
    }

    const mathState: MathState = (inputRef.current as any).mathState;
    const math = [...mathState.exprBounds];

    const uploadFiles = async (
      ts: string,
      encrypted_message: Cipher | undefined,
      filesToUpload: FileUpload[]
    ): Promise<SuccessfulUpload[]> => {
      const success: SuccessfulUpload[] = [];

      const uploaded = [];
      for (let f of filesToUpload) {
        const uri = await uploadToS3(f.original);
        if (uri) {
          let uploaded_file = {
            uri: uri,
            type: f.original.type,
            size: f.original.size,
            name: f.original.name,
          };

          uploaded.push(uploaded_file);
          success.push({ file: f, uri: uri });
        } else {
          throw new Error("Unable to upload a file");
        }
      }

      const uploadRequest = async (message: string, files: unknown[]) => {
        const updateUiPreferences = (message: StoryItem) => {
          setTimeout(() => {
            httpPost("stories", { messages: true, story: item.SK })
              .then((response) => {
                const latest = response.data.messages.find(
                  (e: StoryItem) => e.SK === message.SK
                );
                if (latest && latest.st_ui) {
                  setMessages((prev: StoryItem[]) => {
                    const found = prev.findIndex((e) => e.SK === message.SK);
                    if (found >= 0) {
                      let res = [...prev];
                      res[found] = { ...res[found], st_ui: latest.st_ui };
                      return res;
                    } else {
                      return prev;
                    }
                  });
                }
              })
              .catch((e) => {
                log.debug(e);
              });
          }, 3000);
        };

        type UploadRequest = {
          reply: boolean;
          story: string;
          files?: unknown[];
          refs: StoryRef[];
          enc?: Cipher;
          desc?: string;
          forward?: string;
          math?: Bounds[];
        };

        let obj: UploadRequest = { reply: true, story: item.SK, refs: refs };
        if (files && files.length > 0) {
          obj.files = files;
        }

        if (encrypted_message) {
          obj.enc = encrypted_message;
        } else {
          if (message && message.length > 0) {
            obj.desc = message;
          }
        }

        if (item.st_source) {
          obj.forward = item.st_source;
        }

        if (math && math.length) {
          obj.math = math;
        }

        const response = await httpPost("stories", obj);
        clearMessageInCache(item);
        if (onSent) {
          onSent({ ts: ts, msg: response.data.reply, text: obj.desc });
        }
        if (files && files.length > 0) {
          updateUiPreferences(response.data.reply);
        }
      };

      await uploadRequest(message, uploaded);
      return success;
    }; // uploadFiles

    const ts = u.getCurrentTimestamp();
    let messageObj: MessageItem = {
      PK: "dummy",
      SK: ts,
      ts: ts,
      event_ts: ts,
      dummy: true,
    };

    if (message && !password) {
      messageObj.text = message;
      messageObj.st_pp = preparseTextWithMath(message, math || []);
    }

    if (selectedFiles.length > 0) {
      messageObj.uploaded_files = selectedFiles;
    }

    const proceed = async (projectName: string) => {
      let encrypted_message: Cipher | undefined = undefined;
      if (password && message) {
        encrypted_message = await encrypt(password, message);
      }

      const attemptUpload = async (
        projectName: string,
        message: string,
        password: string,
        files: FileUpload[]
      ) => {
        let ok = true;
        try {
          // testing instrumentation:
          // if (new Date().getTime() % 3 !== 0)
          //   throw new Error();

          const uploads = await uploadFiles(ts, encrypted_message, files);
          await cacheUploadedFiles(projectName, uploads);
        } catch (e) {
          ok = false;
        }

        if (ok) {
          if (onSent) {
            // update the thread, it will remove the dummy messages and
            // render pictures downloaded from cache, timeout is needed in order to give
            // the cache a bit of time to populate, because the cache is async
            setTimeout(() => onSent(), 2000);
          }

          return;
        }

        setMessages((prev: StoryItem[]) => {
          const found = prev.findIndex((m: StoryItem) => m.SK === ts);
          if (found !== undefined) {
            const next = [...prev];
            next[found] = { ...prev[found], error: "upload error" };
            return next;
          }
          return prev;
        });

        toast.error(
          <UploadErrorToast
            onRetry={() => attemptUpload(projectName, message, password, files)}
          />
        );
      };

      setMessages((prev: any[]) =>
        prev ? [...prev, messageObj] : [messageObj]
      );

      attemptUpload(projectName, message, password, selectedFiles);

      if (onSending) {
        onSending();
      }

      updateInput("");
      setDisabled(true);
      setSelectedFiles([]);
      setPassword("");
      clearInputWithMathHighlights(inputRef.current);
    }; // proceed

    proceed(projectName);
  }; // handleSend

  const handleKeyDown = (e: React.KeyboardEvent<HTMLImageElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === "ArrowUp") {
      if (!inputRef?.current?.value) {
        updateInput(previousMessage);
        e.preventDefault();
      }
    } else if (e.key === "Escape") {
      inputRef?.current?.blur();
    }
  }; // handleKeyDown

  const handleKeyUp = () => {}; // handleKey

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processFiles(e.target.files);
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleDelete = useCallback(
    (index: number) => {
      setSelectedFiles((prev) => {
        let next = [...prev];
        next.splice(index, 1);
        return next;
      });
      if (onFileRemoved) {
        onFileRemoved();
      }
    },
    [setSelectedFiles, onFileRemoved]
  );

  const handleBlur = (e: any) => {
    if (inputRef.current?.value === undefined) {
      return;
    }

    const lastSeenValue = inputRef.current.value;
    // give a chance to other events to be handled
    // like when you click on a suggestion, that will immediately trigger blur
    // and you'll loose an event containing the clicked suggestion
    setTimeout(() => {
      const inputValue = inputRef.current?.value;
      const value = inputValue === undefined ? lastSeenValue : inputValue;
      if (!password && value !== undefined) {
        saveMessageInCache(item, value);
      }
      if (onBlur) {
        onBlur(e);
      }
    }, 300);
  };

  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => {
    if (onFocus) {
      onFocus(e);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    handleFilesFromPasteEvent(e, processFiles, setRefs);
  };

  const handleRequestPassword = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? undefined : e.currentTarget));
  };

  const handlePasswordCancel = () => {
    setAnchorEl(undefined);
  };

  const handleSetPassword = (password: string) => {
    setPassword(password);
    setAnchorEl(undefined);
    if (password) {
      clearMessageInCache(item);
    } else {
      saveMessageInCache(item, message);
    }
  };

  useEffect(() => {
    if (inputRef.current && onVisible) {
      /*
      const isInViewport = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        return (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
      };

      if (isInViewport(inputRef.current)) {
        onVisible(true);
      } else {
        onVisible(false);
      }*/

      const observer = new IntersectionObserver(([entry]) => {
        onVisible(entry.isIntersecting);
      });
      observer.observe(inputRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, []);

  useEffect(() => {
    if (droppedFiles && !password) {
      processFiles(droppedFiles);
    }
  }, [droppedFiles]);

  useEffect(() => {
    if (message || (selectedFiles && selectedFiles.length > 0)) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [selectedFiles, message]);

  useEffect(() => {
    if (inputRef?.current) {
      const truncated = message.slice(0, MAX_MESSAGE_SIZE);
      inputRef.current.value = truncated;
      drawMathHighlightsForMathInput(inputRef.current);
    }
  }, [message]);

  return (
    <Stack width="100%">
      <TextFieldWithSuggestions
        label="Want to add anything to the story?"
        width="100%"
        size="small"
        fullWidth
        multiline
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onPaste={handlePaste}
        variant="standard"
        id={anchorEl ? "color-picker-popper" : undefined}
        inputRef={inputRef}
        inputProps={{ maxLength: MAX_MESSAGE_SIZE }}
        InputLabelProps={{ shrink: true }} // [1741]
        InputProps={{
          multiline: true,
          inputComponent: TextareaAutosize,
          endAdornment: (
            <InputAdornment position="end">
              {true && (
                <>
                  <input
                    type="file"
                    id={`file-upload-${item.SK}-${id}`}
                    hidden
                    onChange={handleFileSelected}
                    multiple
                    disabled={!!password}
                  />
                  <label htmlFor={`file-upload-${item.SK}-${id}`}>
                    <IconButton component="span" disabled={!!password}>
                      <AttachFileIcon />
                    </IconButton>
                  </label>
                </>
              )}
              <IconButton
                // edge="end"
                color={password ? undefined : "primary"}
                disabled={selectedFiles.length > 0}
                onClick={handleRequestPassword}
                sx={{ color: password ? "red" : undefined }}
              >
                <KeyIcon />
              </IconButton>
              <IconButton
                edge="end"
                color="primary"
                disabled={disabled || (!!password && selectedFiles.length > 0)}
                onClick={handleSend}
              >
                <SendIcon />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            alignItems: "flex-start",
          },
        }}
      />
      <Popper
        id={anchorEl ? "color-picker-popper" : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
      >
        <PasswordInput
          password={password}
          onPassword={handleSetPassword}
          onCancel={handlePasswordCancel}
          label={"Protect message with password"}
          error={undefined}
          confirm={true}
        />
      </Popper>
      <MemoFileUploadPreview
        files={selectedFiles.map((e) => e.preview || e.original)}
        onDelete={handleDelete}
        readOnly
      />
    </Stack>
  );
}

const MemoFileUploadPreview = memo(FileUploadPreview);
const MemoHighlightBox = memo(HighlightBox);
